import type { MarketSnapshot, LiquidityResponse, GeoResponse } from "../lib/api";

interface Props {
  snapshot?: MarketSnapshot;
  liquidity?: LiquidityResponse;
  geo?: GeoResponse;
}

export function RiskOnOff({ snapshot, liquidity, geo }: Props) {
  const spx = snapshot?.quotes.find((q) => q.symbol === "SPX")?.change_pct ?? 0;
  const vix = snapshot?.quotes.find((q) => q.symbol === "VIX")?.change_pct ?? 0;
  const dxy = snapshot?.quotes.find((q) => q.symbol === "DXY")?.change_pct ?? 0;
  const liq = liquidity?.chg_4w ?? 0;
  const geoRising = (geo?.trackers ?? []).some((t) => t.trend === "rising" && t.value > 60);

  let score = 0;
  if (spx > 0.5) score += 1;
  if (spx < -0.5) score -= 1;
  if (vix > 5) score -= 1;
  if (vix < -5) score += 1;
  if (dxy > 0.3) score -= 0.5;
  if (dxy < -0.3) score += 0.5;
  if (liq > 0) score += 0.5;
  if (liq < 0) score -= 0.5;
  if (geoRising) score -= 0.5;

  let state: "risk-on" | "mixed" | "risk-off";
  let label: string;
  let classes: string;
  if (score >= 1) {
    state = "risk-on";
    label = "Risk-on";
    classes = "border-macro-up/30 bg-macro-up/10 text-macro-up";
  } else if (score <= -1) {
    state = "risk-off";
    label = "Risk-off";
    classes = "border-macro-down/30 bg-macro-down/10 text-macro-down";
  } else {
    state = "mixed";
    label = "Mixed";
    classes = "border-surface-3 bg-surface-2 text-slate-300";
  }

  const soWhat =
    state === "risk-on"
      ? "Risk assets leading; defensive assets lagging."
      : state === "risk-off"
        ? "Defensive assets leading; risk assets under pressure."
        : "Signals are mixed across asset classes.";

  return (
    <div className="flex items-center gap-2">
      <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${classes}`}>
        <span className="relative flex h-2 w-2">
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
              state === "risk-on" ? "bg-macro-up" : state === "risk-off" ? "bg-macro-down" : "bg-slate-400"
            }`}
          />
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              state === "risk-on" ? "bg-macro-up" : state === "risk-off" ? "bg-macro-down" : "bg-slate-400"
            }`}
          />
        </span>
        {label}
      </div>
      <span className="hidden text-[10px] text-slate-500 lg:inline">
        {soWhat} · educational · delayed
      </span>
    </div>
  );
}
